import useStore from "../store.ts";
import type {Component, HideFlags} from "../types.ts";
import {UpdateType} from "../utils/Renderer.ts";
import AsyncRenderWorker from "./RenderWorker.ts?worker";
import type {ItemData, RenderEntry} from "./RenderWorker.ts";
import {collectTransferables, deproxy} from "./WorkerUtils.ts";
import {buildDatasheetRowsIdle} from "../utils/DatasheetUtils.ts";
import updateCoordinator from "../utils/UpdateCoordinator.ts";
import {computeNoData, formatSource} from "../utils/ElementUtils.ts";
import {datasheetRowCapacity} from "../utils/PureElementUtils.ts";
import {renderInvalidationRegistry, RenderInvalidationReason} from "../utils/RenderInvalidation.ts";
import {pipelineMetrics, FRAME_MS, FRAME_BUDGET_MS, GEOMETRY_BUDGET_RATIO} from "../utils/PipelineMetrics.ts";
import {UpdateOrigin, updateContextRegistry} from "../utils/UpdateContextRegistry.ts";
import {orchestrator} from "../utils/Orchestrator.ts";
import type {UpdateContextHandle} from "../utils/UpdateContextRegistry.ts";
import {FeatureFlags} from "../utils/FeatureFlags.ts";

const isBrowser = typeof window !== "undefined";
const getDocument = () => isBrowser ? document : null;

const renderWorkers = new Map<number, Worker>();
let renderInitialized = false;
let renderInitializing = false;
let renderInitPromise: Promise<void> | null = null;
let renderCurrentVersion = 0;
type GeometryTask = { id: string; weight: number; enqueuedAt: number; cause: string };
const geometryPending = new Map<string, GeometryTask>();
const latestSrcById = new Map<string, string>();
/* see docs/EXTRACTED_COMMENTS.md#c797 */
const committedSrcById = new Map<string, string>();

// Coalesce high-frequency render requests (e.g. geometry drain ticks)
let queuedRenderFlushScheduled = false;

// see docs/EXTRACTED_COMMENTS.md#c269
type DeferredRenderBatch = { updates: Record<string, RenderEntry[]>; servedRequestId: number };
const deferredRenderUpdateBatches: DeferredRenderBatch[] = [];

// see docs/EXTRACTED_COMMENTS.md#c270
// see docs/EXTRACTED_COMMENTS.md#c698
let renderAppliedResolvers: Array<{ requestId: number; resolve: () => void }> = [];
let renderRequestSeq = 0;
/* see docs/EXTRACTED_COMMENTS.md#c787 */
let workerDirty = false;

function resolveRenderApplied(servedRequestId: number) {
    if (renderAppliedResolvers.length === 0) return;
    const pending = renderAppliedResolvers;
    renderAppliedResolvers = pending.filter((r) => r.requestId > servedRequestId);
    for (const r of pending) {
        if (r.requestId <= servedRequestId) r.resolve();
    }
}

// see docs/EXTRACTED_COMMENTS.md#c723
let renderWorkSeq = 0;

export function getRenderWorkSeq(): number {
    return renderWorkSeq;
}

export function noteRenderWork(): void {
    renderWorkSeq++;
}

// Unified pipeline tick (replaces all individual timers)
let pipelineTick: any = null;

function startPipeline() {
    if (pipelineTick != null) return;
    pipelineTick = setInterval(() => {
        if (updateCoordinator.hasActiveContexts()) return;

        const frameStart = performance.now();

        drainGeometryBatch(frameStart);
        applyPendingRenderUpdates();

        if (geometryPending.size === 0 &&
            deferredRenderUpdateBatches.length === 0) {
            clearInterval(pipelineTick);
            pipelineTick = null;
        }
    }, FRAME_MS);
}

/* see docs/EXTRACTED_COMMENTS.md#c800 */
function queueRenderRequest(_reason: string = "") {
    noteRenderWork();
    if (queuedRenderFlushScheduled) return;
    queuedRenderFlushScheduled = true;
    queueMicrotask(() => {
        queuedRenderFlushScheduled = false;
        void postRenderRequest();
    });
}

const parsePxLocal = (value?: string | number): number => {
    if (value === undefined || value === null) return 0;
    if (typeof value === "number") return value;
    return parseInt(`${value}`.replace("px", "").replace("100%", "0")) || 0;
};

/* see docs/EXTRACTED_COMMENTS.md#c631 */
const quoteFontFamily = (family?: string): string => {
    const f = `${family ?? ""}`.trim();
    if (!f) return "sans-serif";
    if (f.includes(",") || f.includes('"') || f.includes("'")) return f;
    return `"${f}"`;
};

/* see docs/EXTRACTED_COMMENTS.md#c632 */
const cssFontSize = (size?: string | number): string => {
    if (typeof size === "number") return `${size}px`;
    const s = `${size ?? ""}`.trim();
    if (!s) return "0pt";
    return /^\d+(\.\d+)?$/.test(s) ? `${s}px` : s;
};

/* see docs/EXTRACTED_COMMENTS.md#c633 */
const buildCanvasFont = (style: any): string => {
    const fontStyle = `${style["font-style"] ?? ""}`.trim().toLowerCase();
    const weight = style["font-weight"] || "normal";
    const size = cssFontSize(style["font-size"]);
    const family = quoteFontFamily(style["font-family"]);
    // see docs/EXTRACTED_COMMENTS.md#c634
    const stylePart = fontStyle === "italic" || fontStyle === "oblique" ? `${fontStyle} ` : "";
    return `${stylePart}${weight} ${size} ${family}`;
};


function getGeometryWeight(id: string): number {
    const depth = (id.match(/\./g) || []).length;
    return Math.max(1, 10 - depth);
}

/* see docs/EXTRACTED_COMMENTS.md#c700 */
let measureCtx: CanvasRenderingContext2D | null | undefined;
const measureCache = new Map<string, number>();

export function measureTextWidth(font: string, text: string): number | undefined {
    if (!isBrowser) return undefined;
    if (measureCtx === undefined) {
        measureCtx = getDocument()?.createElement("canvas").getContext("2d") ?? null;
    }
    if (!measureCtx) return undefined;

    const key = `${font}\u0000${text}`;
    const hit = measureCache.get(key);
    if (hit !== undefined) return hit;

    // An unparseable assignment is silently IGNORED, leaving whatever the previous call set.
    // The sentinel makes that detectable (same idiom as Renderer.applyMeasureFont).
    const SENTINEL = "10px sans-serif";
    measureCtx.font = SENTINEL;
    measureCtx.font = font;
    if (measureCtx.font === SENTINEL && font !== SENTINEL) return undefined;

    const m = measureCtx.measureText(text || " ");
    const ink = (m.actualBoundingBoxLeft ?? 0) + (m.actualBoundingBoxRight ?? 0);
    const width = Math.max(m.width, ink);
    if (measureCache.size > 4000) measureCache.clear();
    measureCache.set(key, width);
    return width;
}

// loadFonts does not await its faces, so a layout can be measured against the fallback face and
// that narrower width stays cached (and preferred by the renderer) after the real face lands —
// the text renders wider than its box and overflow clips both ends. Once a family is ready on
// both threads (FONT_READY follows document.fonts.add), drop the cache and re-send the widths of
// every horizontal width owner that uses it; the fontLoaded render request that follows applies them.
function remeasureTextForFamily(family: string) {
    measureCache.clear();
    const store = useStore();
    for (const item of Object.values(store.components)) {
        const parent = item.parent ? store.components[item.parent] : undefined;
        const ownsWidth = (item.viewType === "TEXT_VIEW_EXTEND_HORIZONTAL" && (item.children?.length ?? 0) === 0)
            || parent?.viewType === "TEXT_VIEW_EXTEND_HORIZONTAL";
        if (!ownsWidth) continue;
        const font = buildCanvasFont(item.style);
        if (!font.includes(family)) continue;
        const id = item.props.id;
        const src = latestSrcById.get(id) ?? item.published.src ?? "";
        postUpdateSrc(id, src, measureTextWidth(font, src));
    }
}

// Src updates are staged here and posted as one UPDATE_SRC_BATCH per microtask instead of one
// message per item (a full load sends ~2k). Every other post to the render worker flushes them
// first (flushPendingSrc), so the worker sees the same message order as unbatched posting.
// Last write per id wins, as it did when the worker applied the messages in sequence.
type SrcUpdate = { id: string; src: string; textWidth?: number };
const pendingSrc = new Map<string, SrcUpdate>();
let srcFlushScheduled = false;

function postUpdateSrc(id: string, src: string, textWidth?: number) {
    workerDirty = true;
    pendingSrc.set(id, { id, src, textWidth });
    if (srcFlushScheduled) return;
    srcFlushScheduled = true;
    queueMicrotask(() => {
        srcFlushScheduled = false;
        void flushPendingSrcWhenReady();
    });
}

async function flushPendingSrcWhenReady() {
    if (pendingSrc.size === 0) return;
    await initializeRenderWorkers();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) {
        pendingSrc.clear();
        return;
    }
    flushPendingSrc(renderWorker);
}

function flushPendingSrc(renderWorker: Worker) {
    if (pendingSrc.size === 0) return;
    const srcUpdates = Array.from(pendingSrc.values());
    pendingSrc.clear();
    renderWorker.postMessage({ id: renderCurrentVersion, srcUpdates, messageType: "UPDATE_SRC_BATCH" });
}

// Returns whether a new src was sent (false: unknown item or unchanged src).
export function enqueueRendererSrc(target: string | Component, src: string, reason: string = "srcChanged"): boolean {
    const store = useStore();
    const id = typeof target === "string" ? target : target.props.id;
    const item = typeof target === "string" ? store.components[id] : target;
    if (!item) return false;

    const nextSrc = src ?? "";
    const unchanged = latestSrcById.get(id) === nextSrc;

    // see docs/EXTRACTED_COMMENTS.md#c635
    if (unchanged) {
        return false;
    }

    // see docs/EXTRACTED_COMMENTS.md#c636
    latestSrcById.set(id, nextSrc);

    /* see docs/EXTRACTED_COMMENTS.md#c700 */
    postUpdateSrc(id, nextSrc, measureTextWidth(buildCanvasFont(item.style), nextSrc));
    queueRenderRequest(reason);
    return true;
}


export async function initializeRenderWorkers(): Promise<void> {
    if (!isBrowser) return;
    if (renderInitialized) return;
    if (renderInitializing) return renderInitPromise!;

    renderInitializing = true;
    renderInitPromise = (async () => {
        const maxRenderWorkers = 1; // Single render worker for consistency
        const readyPromises: Promise<void>[] = [];

        for (let i = 0; i < maxRenderWorkers; i++) {
            const worker = new AsyncRenderWorker();
            readyPromises.push(
                new Promise<void>((resolve, reject) => {
                    const onMessage = (e: MessageEvent<any>) => {
                        if (e.data?.type === "RENDER_READY") {
                            worker.removeEventListener("message", onMessage);
                            resolve();
                        }
                    };
                    worker.addEventListener("message", onMessage);
                    worker.addEventListener("error", (err) => {
                        reject(err);
                    });
                    worker.postMessage({ messageType: "INIT" });
                })
            );

            worker.onmessage = (e: MessageEvent<any>) => {
                const msg = e.data;
                switch (msg.type) {
                    case "RENDER_UPDATE": {
                        const updates: Record<string, RenderEntry[]> = msg.updates || {};
                        // Absent id (older worker build / unsolicited pass) must never
                        // strand an awaiter — treat it as serving everything. #c660
                        const servedRequestId: number = typeof msg.servedRequestId === "number"
                            ? msg.servedRequestId
                            : Number.POSITIVE_INFINITY;
                        /* see docs/EXTRACTED_COMMENTS.md#c701 */
                        const batchVersion: number = typeof msg.version === "number"
                            ? msg.version
                            : renderCurrentVersion;
                        // see docs/EXTRACTED_COMMENTS.md#c637
                        try {
                            if (batchVersion < renderCurrentVersion) {
                                console.warn(
                                    `[RenderWorkerManager] dropped RENDER_UPDATE computed under layout version `
                                    + `${batchVersion} (current ${renderCurrentVersion}): `
                                    + `${Object.keys(updates).length} item(s)`
                                );
                            } else {
                                /* see docs/EXTRACTED_COMMENTS.md#c702 */
                                deferredRenderUpdateBatches.push({ updates, servedRequestId });
                                if (updateCoordinator.hasActiveContexts()) {
                                    startPipeline();
                                } else {
                                    applyPendingRenderUpdates();
                                }
                            }
                        } finally {
                            worker.postMessage({ messageType: "RENDER_ACK", batchId: msg.batchId });
                        }
                        break;
                    }
                    case "FONT_READY": {
                        const store = useStore();
                        const family = msg.family as string | undefined;
                        if (family) {
                            store.fonts.workerReady.add(family);
                            remeasureTextForFamily(family);
                        }
                        // see docs/EXTRACTED_COMMENTS.md#c638
                        queueRenderRequest("fontLoaded");
                        break;
                    }
                }
            };
            // see docs/EXTRACTED_COMMENTS.md#c639
            worker.addEventListener("error", (event) => {
                if (!renderInitialized) return;
                console.error("[RenderWorkerManager] render worker error:", event);
                // The worker is down; no request will ever be served. Release every
                // awaiter rather than leaving callers blocked on a dead worker. #c660
                resolveRenderApplied(Number.POSITIVE_INFINITY);
            });

            renderWorkers.set(i, worker);
        }

        await Promise.all(readyPromises);
        renderInitialized = true;
        renderInitializing = false;
    })();

    await renderInitPromise;
}

export function getRenderWorker() {
    Promise.resolve().then(() => initializeRenderWorkers());
    return renderWorkers.get(0);
}

/* see docs/EXTRACTED_COMMENTS.md#c640 */
function flushPendingGeometry(renderWorker: Worker) {
    flushPendingSrc(renderWorker);
    if (geometryPending.size === 0) return;
    const store = useStore();
    const items: ItemData[] = [];
    for (const t of geometryPending.values()) {
        const item = store.components[t.id];
        if (!item) continue;
        const geometryItem = buildGeometryItem(item);
        if (!geometryItem) continue;
        items.push(geometryItem);
    }
    geometryPending.clear();
    if (items.length === 0) return;
    workerDirty = true;
    renderWorker.postMessage({ id: renderCurrentVersion, items, messageType: "UPDATE_GEOMETRY_BATCH" });
}

/* see docs/EXTRACTED_COMMENTS.md#c641 */
/* see docs/EXTRACTED_COMMENTS.md#c800 */
export async function postRenderRequest(_reasons: string[] = [], _impactedIds: string[] = []): Promise<void> {
    noteRenderWork();
    await initializeRenderWorkers();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) {
        return;
    }
    flushPendingGeometry(renderWorker);

    // see docs/EXTRACTED_COMMENTS.md#c787
    if (!workerDirty && renderAppliedResolvers.length > 0) {
        const newest = renderAppliedResolvers[renderAppliedResolvers.length - 1].requestId;
        return new Promise<void>((resolve) => {
            renderAppliedResolvers.push({ requestId: newest, resolve });
        });
    }
    workerDirty = false;

    // see docs/EXTRACTED_COMMENTS.md#c642
    // see docs/EXTRACTED_COMMENTS.md#c698
    const requestId = ++renderRequestSeq;
    const applied = new Promise<void>((resolve) => {
        renderAppliedResolvers.push({ requestId, resolve });
    });

    renderWorker.postMessage({ id: renderCurrentVersion, requestId, messageType: "TRAVERSE_GEOMETRY_TREE" });
    return applied;
}

/**
 * Compatibility wrapper for legacy callers.
 * Prefer using RenderInvalidationRegistry.requestRender.
 */
export async function postTraversal() {
    return postRenderRequest();
}

// Synchronous: only enqueues; the pipeline tick posts once workers exist.
export function postUpdateGeometry(id: string, cause: string = "unspecified"): void {
    noteRenderWork();
    if (!id) return;
    void initializeRenderWorkers();
    if (!geometryPending.has(id)) {
        const task: GeometryTask = { id, weight: getGeometryWeight(id), enqueuedAt: (typeof performance !== "undefined" ? performance.now() : Date.now()), cause };
        geometryPending.set(id, task);
    }
    startPipeline();
}

export async function postAnchors(anchors: any) {
    await initializeRenderWorkers();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) return;
    // see docs/EXTRACTED_COMMENTS.md#c643
    const serializableAnchors = Object.fromEntries(
        Object.entries(anchors).map(([key, value]) => [
            key,
            value instanceof Set ? Array.from(value) : value
        ])
    );

    workerDirty = true;
    flushPendingSrc(renderWorker);
    renderWorker.postMessage({ id: renderCurrentVersion, anchors: { anchors: serializableAnchors }, messageType: "UPDATE_ANCHORS" });
    // Anchors affect layout; request a traversal.
    postRenderRequest(["anchorUpdate"], Array.from(Object.keys(serializableAnchors)));
}

export async function postLayout(layout_id: string) {
    await initializeRenderWorkers();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) return;

    // Staged src belongs to the outgoing layout: post it under the old version, ahead of the swap.
    flushPendingSrc(renderWorker);
    // see docs/EXTRACTED_COMMENTS.md#c644
    renderCurrentVersion++;
    if (deferredRenderUpdateBatches.length > 0) {
        console.warn(`[RenderWorkerManager] postLayout(${layout_id}) discarded ${deferredRenderUpdateBatches.length} deferred render batch(es)`);
    }
    deferredRenderUpdateBatches.length = 0;
    geometryPending.clear();
    latestSrcById.clear();
    committedSrcById.clear();
    /* see docs/EXTRACTED_COMMENTS.md#c740 */
    pendingDatasheetTriggers.clear();

    workerDirty = true;
    renderWorker.postMessage({ id: renderCurrentVersion, layout: { layout_id }, messageType: "UPDATE_LAYOUT" });
    // Ensure an initial render after layout swap.
    postRenderRequest(["layoutLoaded"]);
}

export async function postFont(name: string, buffer: ArrayBuffer, descriptors?: FontFaceDescriptors) {
    await initializeRenderWorkers();

    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) return;
    const payload = {
        font: {
            name, buffer, descriptors
        }, messageType: 'ADD_FONT'
    };
    workerDirty = true;
    flushPendingSrc(renderWorker);
    // The only message that carries a buffer worth transferring.
    renderWorker.postMessage(payload, collectTransferables(payload));
    // see docs/EXTRACTED_COMMENTS.md#c645
}

export async function postDeleteItems(ids: string[]) {
    if (ids.length === 0) return;
    await initializeRenderWorkers();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) return;

    workerDirty = true;
    flushPendingSrc(renderWorker);
    renderWorker.postMessage({ ids, messageType: "DELETE_ITEMS" });
    for (const id of ids) {
        latestSrcById.delete(id);
        committedSrcById.delete(id);
    }
    queueRenderRequest("itemsDeleted");
}

/* see docs/EXTRACTED_COMMENTS.md#c646 */
/* see docs/EXTRACTED_COMMENTS.md#c790 */
export async function postForceUpdatePositionAndRender(_parentId: string, _reasons: string[] = ["datasheetRebuild"]): Promise<void> {
    await initializeRenderWorkers();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) return;

    flushPendingGeometry(renderWorker);
    workerDirty = false;

    // see docs/EXTRACTED_COMMENTS.md#c698
    const requestId = ++renderRequestSeq;
    const applied = new Promise<void>((resolve) => {
        renderAppliedResolvers.push({ requestId, resolve });
    });

    renderWorker.postMessage({ id: renderCurrentVersion, requestId, messageType: "TRAVERSE_GEOMETRY_TREE" });
    return applied;
}

export async function postDimensions() {
    await initializeRenderWorkers();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) return;

    const doc = getDocument();
    const width = doc?.documentElement.clientWidth ?? 1920;
    const height = doc?.documentElement.clientHeight ?? 1080;

    workerDirty = true;
    flushPendingSrc(renderWorker);
    renderWorker.postMessage({ id: renderCurrentVersion, dimensions: { width, height }, messageType: "UPDATE_DIMENSIONS" });
    postRenderRequest(["dimensionChange"]);
}

export type DatasheetBuildSource = "initial" | "reconnect" | "extend" | "bail-sweep";

/* see docs/EXTRACTED_COMMENTS.md#c735 */
function buildBailedDatasheets(store: ReturnType<typeof useStore>): void {
    /* see docs/EXTRACTED_COMMENTS.md#c751 */
    for (const sheetId of [...(store.datasheets ?? [])].sort()) {
        const sheet = store.components[sheetId];
        if (!sheet) continue;
        if (sheet.flags?.calculated_row !== false) continue;
        if (datasheetRowCapacity(sheet, store.components) <= 0) continue;
        triggerDatasheetBuild(sheetId, "bail-sweep");
    }
}

/* see docs/EXTRACTED_COMMENTS.md#c740 */
const pendingDatasheetTriggers = new Set<string>();

/* see docs/EXTRACTED_COMMENTS.md#c647 */
export function triggerDatasheetBuild(itemID: string, _source: DatasheetBuildSource): void {
    const store = useStore();
    const item = store.components[itemID];
    if (!item) {
        console.warn(`[DatasheetBuild] skip ${itemID} (source=${_source}): component missing from store`);
        return;
    }
    if (!item.flags) item.flags = {} as any;
    if (item.flags.calculated_row) {
        console.warn(`[DatasheetBuild] skip ${itemID} (source=${_source}): calculated_row already true`);
        return;
    }
    /* see docs/EXTRACTED_COMMENTS.md#c740 */
    if (pendingDatasheetTriggers.has(itemID)) return;
    console.log(`[DatasheetBuild] trigger ${itemID} (source=${_source})`);
    let resolveFunc: Function | null = null;
    const promise = new Promise<void>((resolve) => {
        resolveFunc = resolve;
    });
    pendingDatasheetTriggers.add(itemID);
    orchestrator.enqueue(UpdateOrigin.DatasheetBuild, async (handle) => {
        pendingDatasheetTriggers.delete(itemID);
        if (resolveFunc) {
            resolveFunc();
        }
        // The flag is set when the build actually STARTS, not when it is
        // requested: a queued-but-dropped build (queue clear) must not latch
        // calculated_row=true with no build coming, or the sheet becomes
        // permanently unbuildable. Two triggers racing before the body starts
        // enqueue two builds; the second hits buildDatasheetRowsIdle's
        // children >= limit early-return.
        const current = store.components[itemID];
        if (!current) return;
        if (!current.flags) current.flags = {} as any;
        current.flags.calculated_row = true;
        console.log(`[DatasheetBuild] body running ${itemID} (source=${_source}, handle.active=${handle.active})`);
        await buildDatasheetRowsIdle(item, store,   (c: Component) => postUpdateGeometry(c.props.id, "datasheet-build"), promise, handle);
        await postUpdateGeometry(item.props.id, "render-rows");
        console.log(`[DatasheetBuild] body done ${itemID} (source=${_source}, children=${item.children?.length ?? 0})`);
    }).catch((err) => {
        // Dropped by a queue clear (or the build threw): expected during
        // teardown/overflow recovery; the flag was never latched, so a later
        // trigger rebuilds normally.
        pendingDatasheetTriggers.delete(itemID);
        console.warn(`[DatasheetBuild] ${itemID} (source=${_source}) did not complete:`, err?.message ?? err);
    });
}

/** Per-batch SUPPORTED_ROWS values, drained after seal. */
const supportedRowsSeen = new Map<string, number>();
/* see docs/EXTRACTED_COMMENTS.md#c799 */
let batchGeometryApplied = false;
let batchFlagsCommitted = false;

/** Hide flags the RenderWorker recomputes every tick; the main thread stages all the others. */
const RENDERER_OWNED_HIDE_FLAGS = ["render", "anchoring", "dedup", "children"] as const satisfies readonly (keyof HideFlags)[];

function applyUpdates(store: ReturnType<typeof useStore>, itemID: string, updates: RenderEntry[], _handle?: UpdateContextHandle): boolean {
    const item = store.components[itemID];
    if (!item) return false;
    // see docs/EXTRACTED_COMMENTS.md#c800
    for (const [type, value] of updates) {
        switch (type as UpdateType) {
            case UpdateType.X: {
                const next = `${parsePxLocal(value)}px`;
                if (item.props.style.left === next) break;
                item.props.style.left = next;
                batchGeometryApplied = true;
                if (item?.anchor?.anchor_id && store.components[item.anchor.anchor_id]?.style?.position == "relative") {
                    item.props.style.position = 'static';
                } else {
                    item.props.style.position = 'absolute';
                }
                break;
            }
            case UpdateType.Y: {
                const next = `${parsePxLocal(value)}px`;
                if (item.props.style.top === next) break;
                item.props.style.top = next;
                batchGeometryApplied = true;
                if (item?.anchor?.anchor_id && store.components[item.anchor.anchor_id]?.style?.position == "relative") {
                    item.props.style.position = 'static';
                } else {
                    item.props.style.position = 'absolute';
                }
                break;
            }
            case UpdateType.WIDTH: {
                const next = `${parsePxLocal(value)}px`;
                if (item.props.style.width === next) break;
                item.props.style.width = next;
                batchGeometryApplied = true;
                break;
            }
            case UpdateType.HEIGHT: {
                const next = `${parsePxLocal(value)}px`;
                if (item.props.style.height === next) break;
                item.props.style.height = next;
                batchGeometryApplied = true;
                break;
            }
            case UpdateType.HIDE_FLAGS_COMMIT: {
                batchFlagsCommitted = true;
                const nextFlags = value || {};
                const childrenFlipped = 'children' in nextFlags
                    && item.published.hide_flags?.children !== nextFlags.children;
                // Staged flags take only the renderer-owned keys. A commit echoes every flag the
                // worker last saw, so a lagging one would put back a main-thread flag (data, rule,
                // image, ...) staged since, and the next snapshot would ship that stale value.
                const staged: Partial<HideFlags> = {};
                for (const key of RENDERER_OWNED_HIDE_FLAGS) {
                    if (key in nextFlags) staged[key] = nextFlags[key];
                }
                item.hide_flags = {...item.hide_flags, ...staged};
                if (!item.published.hide_flags) item.published.hide_flags = { ...item.hide_flags };
                item.published.hide_flags = {...item.published.hide_flags, ...nextFlags};
                if (childrenFlipped) {
                    // A children-visibility flip changes layout the same way the old
                    // updateHidden realization did — invalidate the subtree so autosize
                    // ancestors and overlap peers recompute.
                    renderInvalidationRegistry.requestRender(
                        RenderInvalidationReason.VisibilityChange,
                        [itemID],
                        { shallow: false }
                    );
                }
                break;
            }
            case UpdateType.SRC: {
                let newSrc = value;
                // see docs/EXTRACTED_COMMENTS.md#c648
                const latest = latestSrcById.get(itemID);
                if (latest !== undefined && latest !== newSrc) break;

                const hadSrc = !!item.published.src;
                /* see docs/EXTRACTED_COMMENTS.md#c797 */
                committedSrcById.set(itemID, newSrc);
                newSrc = formatSource(newSrc, "");
                item.published.src = newSrc;

                if (!hadSrc && newSrc && item.hide_flags.image) {
                    item.hide_flags.image = false;
                    postUpdateGeometry(itemID, "src_restored");
                }
                break;
            }
            case UpdateType.SUPPORTED_ROWS: {
                supportedRowsSeen.set(itemID, Number(value) || 0);
                break;
            }
        }
    }
    return true;
}

/* see docs/EXTRACTED_COMMENTS.md#c656 */
function resyncGeometry(itemIDs: Iterable<string>, cause: string) {
    for (const itemID of itemIDs) {
        void postUpdateGeometry(itemID, cause);
    }
}

export function handleRenderUpdates(itemUpdates: Record<string, RenderEntry[]>) {
    const handle = updateContextRegistry.create(UpdateOrigin.RenderUpdate);
    // see docs/EXTRACTED_COMMENTS.md#c651
    updateCoordinator.removeFromActiveStack(handle.id);
    const store = useStore();
    const entries = Object.entries(itemUpdates);

    for (const [itemID] of entries) {
        handle.register(itemID);
    }
    const failed: string[] = [];
    supportedRowsSeen.clear();
    batchGeometryApplied = false;
    batchFlagsCommitted = false;
    for (const [itemID, updates] of entries) {
        // see docs/EXTRACTED_COMMENTS.md#c652
        try {
            if (!applyUpdates(store, itemID, updates, handle)) failed.push(itemID);
        } catch (err) {
            // A throw abandons the REST of this item's updates. The renderer has already recorded
            // them as published, so re-seed its ground truth below or they are never re-emitted.
            const types = (updates || []).map((u) => u?.[0]);
            console.error(`[RenderWorkerManager] applyUpdates threw for ${itemID} (types=${types.join(",")}) — remaining updates for this item dropped:`, err);
            failed.push(itemID);
        } finally {
            handle.complete(itemID);
        }
    }
    updateContextRegistry.seal(handle);
    // see docs/EXTRACTED_COMMENTS.md#c666
    for (const [sheetId, supported] of supportedRowsSeen) {
        store.socketManager.layoutManager?.scheduleRowUsageRecount(sheetId, supported);
    }
    /* see docs/EXTRACTED_COMMENTS.md#c750 */
    /* see docs/EXTRACTED_COMMENTS.md#c799 */
    if (supportedRowsSeen.size > 0 || batchFlagsCommitted) {
        store.socketManager.layoutManager?.reconcileChainStarts();
    }
    supportedRowsSeen.clear();
    // Anything main could not apply must be re-serialized: buildGeometryItem re-seeds
    // public_geometry from the real style, so the renderer sees the divergence and re-emits.
    if (failed.length > 0) resyncGeometry(failed, "apply-failed");
    /* see docs/EXTRACTED_COMMENTS.md#c735 */
    /* see docs/EXTRACTED_COMMENTS.md#c799 */
    if (batchGeometryApplied) buildBailedDatasheets(store);
}

/* see docs/EXTRACTED_COMMENTS.md#c681 */
export function isVariableRowSheet(item: Component): boolean {
    if (item.type !== "DataObjectDatasheet") return false;
    return FeatureFlags.variableRowHeights;
}

function buildGeometryItem(item: Component): ItemData | null {
    const style: any = item.style;

    const font = buildCanvasFont(style);
    /* see docs/EXTRACTED_COMMENTS.md#c797 */
    const id = item.props.id;
    const desiredSrc = latestSrcById.get(id) ?? item.published.src ?? "";
    const committedSrc = committedSrcById.get(id) ?? item.published.src ?? "";

    const borderLeft = parsePxLocal(style['border-left-width']) || 0;
    const borderRight = parsePxLocal(style['border-right-width']) || 0;
    const borderTop = parsePxLocal(style['border-top-width']) || 0;
    const borderBottom = parsePxLocal(style['border-bottom-width']) || 0;

    const borderX = borderLeft + borderRight;
    const borderY = borderTop + borderBottom;

    return {
        id: item.props.id,
        type: item.type,
        view_type: item.viewType,
        auto_size: item.auto_size,
        children: item.children.map((a) => a.props.id),
        anchor: deproxy(item.anchor),
        hide_flags: deproxy(item.hide_flags),
        public_hide_flags: deproxy(item.published.hide_flags),
        // see docs/EXTRACTED_COMMENTS.md#c792
        geometry: {
            position: item.props.style.position,
            width: parsePxLocal(item.props.style.width),
            height: parsePxLocal(item.props.style.height),
            borderX: borderX,
            borderY: borderY,
            x: parsePxLocal(item.props.style.left),
            y: parsePxLocal(item.props.style.top),
            original: {
                width: parsePxLocal(item.style.width),
                height: parsePxLocal(item.style.height),
                x: parsePxLocal(item.style.left),
                y: parsePxLocal(item.style.top),
            },
        },
        // Ground truth for the renderer's emit dedup: what main has actually committed. Re-seeded on
        // every serialization, so any geometry the renderer believed it published but main never
        // applied re-diverges here and gets re-emitted.
        public_geometry: {
            x: parsePxLocal(item.props.style.left),
            y: parsePxLocal(item.props.style.top),
            width: parsePxLocal(item.props.style.width),
            height: parsePxLocal(item.props.style.height),
        },
        // see docs/EXTRACTED_COMMENTS.md#c653
        text: desiredSrc,
        public_text: committedSrc,
        font,
        /* see docs/EXTRACTED_COMMENTS.md#c700 */
        textAdvance: measureTextWidth(font, desiredSrc),
        textAdvanceFor: desiredSrc,
        omit_duplicate: item.dynamic?.omit_duplicate ?? false,
        auto_hide: item.auto_hide,
        overlay: item.overlay,
        no_data: computeNoData(item),
        template_resolved: item.flags?.template_resolved,
        // see docs/EXTRACTED_COMMENTS.md#c680
        variable_rows: isVariableRowSheet(item),
    };
}

function drainGeometryBatch(frameStart: number) {
    if (geometryPending.size === 0) return;

    const store = useStore();
    const renderWorker = renderWorkers.get(0);
    if (!renderWorker) return;

    const budget = FRAME_BUDGET_MS * GEOMETRY_BUDGET_RATIO;
    const tasks = Array.from(geometryPending.values());
    tasks.sort((a, b) => (b.weight - a.weight) || (a.enqueuedAt - b.enqueuedAt));

    const items: ItemData[] = [];
    const serStart = performance.now();
    for (const t of tasks) {
        if (performance.now() - frameStart > budget) break;

        const item = store.components[t.id];
        geometryPending.delete(t.id);
        if (!item) continue;
        const geometryItem = buildGeometryItem(item);
        if (!geometryItem) continue;
        items.push(geometryItem);
    }
    const serEnd = performance.now();
    if (items.length > 0) {
        pipelineMetrics.recordGeometrySerialization(items.length, serEnd - serStart);
    }

    if (items.length === 0) return;

    workerDirty = true;
    flushPendingSrc(renderWorker);
    renderWorker.postMessage({ id: renderCurrentVersion, items, messageType: "UPDATE_GEOMETRY_BATCH" });
    queueRenderRequest("geometryChanged");
}

/* see docs/EXTRACTED_COMMENTS.md#c800 */
function mergeDeferredBatches(batches: DeferredRenderBatch[]): Record<string, RenderEntry[]> {
    if (batches.length === 1) return batches[0].updates;
    const byItem: Record<string, Map<string, unknown>> = {};
    for (const batch of batches) {
        for (const [id, entries] of Object.entries(batch.updates)) {
            const byType = (byItem[id] ??= new Map<string, unknown>());
            for (const [type, value] of entries) {
                if (type === UpdateType.HIDE_FLAGS_COMMIT && byType.has(type)) {
                    const prev = byType.get(type) as Record<string, unknown>;
                    byType.delete(type);
                    byType.set(type, { ...prev, ...(value as Record<string, unknown>) });
                    continue;
                }
                byType.delete(type);
                byType.set(type, value);
            }
        }
    }
    const merged: Record<string, RenderEntry[]> = {};
    for (const [id, byType] of Object.entries(byItem)) {
        merged[id] = Array.from(byType.entries());
    }
    return merged;
}

function applyPendingRenderUpdates() {
    if (deferredRenderUpdateBatches.length === 0) return;

    // Highest request served by any batch in this merge — awaiters at or below it are
    // satisfied once handleRenderUpdates has applied the merged result. #c660
    const batches = deferredRenderUpdateBatches.splice(0);
    let servedRequestId = 0;
    for (const entry of batches) {
        servedRequestId = Math.max(servedRequestId, entry.servedRequestId);
    }
    const merged = mergeDeferredBatches(batches);
    try {
        handleRenderUpdates(merged);
        resolveRenderApplied(servedRequestId);
    } catch (err) {
        // A throw here loses the WHOLE batch (geometry, hide flags, src). Never swallow it
        // silently, and re-seed the renderer's published-geometry ground truth for every item
        // in it so the geometry re-emits. Awaiters stay pending on purpose: the resync produces
        // a fresh pass whose servedRequestId is >= this one, so they release there.
        console.error(`[RenderWorkerManager] RENDER_UPDATE batch dropped (${Object.keys(merged).length} items):`, err);
        resyncGeometry(Object.keys(merged), "batch-dropped");
    }
}



